import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@mobiscroll/react-latest'
import { ClipLoader } from 'react-spinners'
import { notification } from 'src/components/riders/rider-portal/common/Notification'
import DriverAndVehicle from 'src/components/riders/rider-portal/trips/details/DriverAndVehicle'
import FareAndPrice from 'src/components/riders/rider-portal/trips/details/FareAndPrice'
import Label from 'src/components/riders/rider-portal/common/Label'
import LocationItem from 'src/components/riders/rider-portal/trips/details/LocationItem'
import PassengersAndPaymentMethods from 'src/components/riders/rider-portal/trips/details/PassengersAndPaymentMethods'
import PortalContainer from 'src/components/riders/rider-portal/common/PortalContainer'
import PortalComponent from 'src/components/riders/rider-portal/common/PortalComponent'
import RideDuration from 'src/components/riders/rider-portal/trips/details/RideDuration'
import RiderPortalMapView from 'src/components/riders/rider-portal/google/map/RiderPortalMapView'
import ServiceAndProduct from 'src/components/riders/rider-portal/trips/details/ServiceAndProduct'
import Skeleton from 'src/components/riders/rider-portal/common/Skeleton'
import { secondsToHoursMinutes } from 'src/utils'
import { LOCAL_STORAGE_RIDER_PORTAL_CURRENT_USER_KEY } from 'src/utils/auth'
import { IPortalTripDetailsProps } from 'src/types/rider-portal'
import { RIDER_PORTAL_UI_MESSAGES } from 'src/constants/rider-portal'
import {
  activeTripStatuses,
  activeTripStatusesWithDriver,
  cancelledTripsStatusesDashboard,
  completedTripStatusesDashboard,
  issuesTripStatuses,
  PORTAL_TRIP_STATUS_LABEL_AND_COLOR,
  TripStatuses
} from 'src/constants/trip-statuses'

let estimatedPickupTime = 0
const TripDetails = ({
  fetchingTripDetails,
  trip,
  tripId,
  updatingTrip,
  cancelTrip,
  getTripDetails,
  resetCurrentlyViewedTripsData
}: IPortalTripDetailsProps) => {
  const [isCurrentUserTrip, setIsCurrentUserTrip] = useState(true)

  const NOT_CANCELLABLE_TRIP_STATUS = [
    ...activeTripStatusesWithDriver,
    ...cancelledTripsStatusesDashboard,
    ...completedTripStatusesDashboard,
    ...issuesTripStatuses
  ]
  const SCHEDULED_TRIP_STATUS = [
    TripStatuses.SCHEDULED,
    TripStatuses.SCHEDULE_REQUESTED,
    TripStatuses.SCHEDULE_ACCEPTED
  ]
  const driveTimeIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const getTripDetailsIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const {
    averageEta,
    dropoffLocation,
    estimatedDuration,
    estimatedFare,
    estimatedPrice,
    notes,
    operator,
    operatorLocation,
    pickupLocation,
    product,
    rider,
    service,
    status,
    tripsGroupRides,
    vehicle
  } = trip
  const operatorCoordinates = (operatorLocation && operatorLocation.coordinates) || []
  const tripStatus = status && status.length > 0 && status[0].status

  useEffect(() => {
    // Check if current trip belongs to logged in user
    const riderDetails = localStorage.getItem(LOCAL_STORAGE_RIDER_PORTAL_CURRENT_USER_KEY) || ''
    const { id = '' } = JSON.parse(riderDetails)
    const { riderId = '' } = rider || ''
    if (id && riderId && id !== riderId) {
      setIsCurrentUserTrip(false)
      notification({ color: 'danger', message: 'The trip is not assigned to the current logged-in rider' })
    }
  }, [tripStatus])

  useEffect(() => {
    if (tripId) {
      const INTERVAL = tripStatus === TripStatuses.WAITING ? 60000 : 10000

      // Fetch trip details including realtime updates(e.g. trip status, operator)
      // Invoke immediately(initial fetch)
      fetchTripDetails(tripId, true)

      // Then every INTERVAL if trip is active only
      if (
        activeTripStatuses.includes(tripStatus ? tripStatus : '') ||
        SCHEDULED_TRIP_STATUS.includes(tripStatus ? tripStatus : '')
      ) {
        getTripDetailsIntervalRef.current = setInterval(() => {
          fetchTripDetails(tripId, false)
        }, INTERVAL)
      }
      // End of fetch trip details

      if (averageEta && averageEta !== 0) {
        estimatedPickupTime = averageEta
      }
    }

    // Clean up on unmount
    return () => {
      if (getTripDetailsIntervalRef.current) {
        clearInterval(getTripDetailsIntervalRef.current)
      }
      if (driveTimeIntervalRef.current) {
        clearInterval(driveTimeIntervalRef.current)
      }
      if (trip.id) {
        resetCurrentlyViewedTripsData()
      }
    }
  }, [tripId, tripStatus])

  const fetchTripDetails = (tripId: string, initialFetch: boolean) => {
    getTripDetails(tripId, initialFetch)
      .then(data => {
        if (!data.id) {
          notification({
            color: 'danger',
            message: 'Failed to fetch trip details, check the logs for more information'
          })
        }
      })
      .catch(() => {
        notification({
          color: 'danger',
          message: 'Failed to fetch trip details, check the logs for more information'
        })
      })
  }

  const dropOff = dropoffLocation
    ? {
        fullAddress: dropoffLocation.fullAddress,
        latitude: dropoffLocation.latitude,
        longitude: dropoffLocation.longitude
      }
    : null
  const pickUp = pickupLocation
    ? {
        fullAddress: pickupLocation.fullAddress,
        latitude: pickupLocation.latitude,
        longitude: pickupLocation.longitude
      }
    : null
  const locationArr = [
    { ...(pickUp !== null ? pickUp : { fullAddress: '' }) },
    { ...(dropOff !== null ? dropOff : { fullAddress: '' }) }
  ]

  const {
    common,
    dataFeedback,
    trips: { details }
  } = RIDER_PORTAL_UI_MESSAGES

  const handleTripCancellation = () => {
    cancelTrip(tripId).then(({ success, message }) => {
      notification({ color: success ? 'success' : 'danger', message })
      fetchTripDetails(tripId, false)
    })
  }

  return (
    <PortalContainer>
      <PortalComponent title={details.tripDetails}>
        {fetchingTripDetails ? (
          <div className='rp-trip-details-skeleton-wrapper'>
            <Skeleton text={dataFeedback.retrievingTripDetails} />
          </div>
        ) : (
          <>
            {!isCurrentUserTrip || !trip.id ? (
              <div className='rp-trip-details-skeleton-wrapper'>
                <Skeleton text={dataFeedback.noDataAvailable} withLoading={false} />
              </div>
            ) : (
              <div className='rp-trip-details-container'>
                <div className='rp-mb24 rp-mt8'>
                  {locationArr.map(({ fullAddress }, index) => (
                    <LocationItem address={fullAddress} key={index} type={index === 0 ? 'pickUp' : 'dropOff'} />
                  ))}
                </div>
                <div className='rp-trip-map-container'>
                  <RiderPortalMapView
                    center={null}
                    className='b-rad-8'
                    operator={
                      operatorCoordinates.length > 0 && activeTripStatuses.includes(tripStatus ? tripStatus : '')
                        ? { latitude: operatorCoordinates[1], longitude: operatorCoordinates[0] }
                        : null
                    }
                    dropOff={dropOff}
                    pickUp={pickUp}
                    showPolyline={true}
                  />
                </div>
                {tripStatus && (
                  <div className='rp-trip-status-container rp-mt24'>
                    <h3
                      className={`font-white my0 rp-p12 text-center text-lg weight-500 bg-${
                        PORTAL_TRIP_STATUS_LABEL_AND_COLOR[tripStatus].color
                      }`}
                    >
                      {PORTAL_TRIP_STATUS_LABEL_AND_COLOR[tripStatus].label}
                    </h3>
                    {(estimatedPickupTime !== 0 || notes) && (
                      <div className='rp-mt24'>
                        {estimatedPickupTime !== 0 && tripStatus === TripStatuses.TO_PICK_UP && (
                          <div className='flex column-gap-12 justify-center'>
                            <Label text={`${details.estimatedPickupTime}`} />
                            <p className='font-grey8 rp-my0 rp-pt2 text-md'>{`${secondsToHoursMinutes(
                              estimatedPickupTime
                            )}`}</p>
                          </div>
                        )}
                        {notes && (
                          <div className='flex column-gap-12 justify-center rp-mt8'>
                            <Label text={`${common.notes}`} />
                            <p className='font-grey8 rp-my0 rp-pt2 text-md'>{notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className='rp-trip-details rp-mb8 rp-mt24'>
                  {service && product && <ServiceAndProduct product={product.name} service={service.name} />}
                  {operator && vehicle && <DriverAndVehicle operator={operator} vehicle={vehicle} />}
                  {estimatedFare && estimatedPrice && <FareAndPrice fare={estimatedFare} price={estimatedPrice} />}
                  {tripsGroupRides.length > 0 && <PassengersAndPaymentMethods passengers={tripsGroupRides} />}
                  {estimatedDuration && <RideDuration duration={estimatedDuration} />}
                </div>
                {tripStatus && !NOT_CANCELLABLE_TRIP_STATUS.includes(tripStatus) && (
                  <div className='rp-mt24'>
                    <Button
                      className='mbsc-flat-btn w100'
                      disabled={updatingTrip}
                      onClick={() => handleTripCancellation()}
                    >
                      {updatingTrip ? (
                        <ClipLoader size={24} sizeUnit={'px'} color={'#405264'} loading={updatingTrip} />
                      ) : (
                        `${common.cancel} ${common.trip}`
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </PortalComponent>
    </PortalContainer>
  )
}

export default TripDetails



// WEBPACK FOOTER //
// ./src/components/riders/rider-portal/trips/details/TripDetails.tsx